import { useState, useCallback } from "react"
import cn from "classnames"
import Image from "components/Image"
import useWindowSize from "utilities/useWindowSize"
import { track } from "utilities/tracking"
import Arrow from "public/assets/chevron-teal.svg"
import Close from "public/assets/close-x.svg"

export default function GalleryModal({ images, closeModal, focusRef }) {
  const [selectedImage, setSelectedImg] = useState(0)

  const { width } = useWindowSize()
  const visibleSlides = useCallback(() => {
    if (width >= 1024 && images.length >= 6) return 6
    if (width >= 1024 && images.length > 2) return images.length
    return 1
  }, [images, width])

  const [currentSlide, setSlide] = useState(0)
  if (!images?.length) return null

  const slideWidth = 126

  function moveSlidesLeft() {
    track({ event: "gallery_move_left" })
    if (currentSlide !== 0) {
      setSlide(currentSlide - slideWidth)
      setSelectedImg(selectedImage - 1)
    } else {
      if (selectedImage === 0) {
        setSlide(images.length * slideWidth - visibleSlides() * slideWidth)
        setSelectedImg(images.length - 1)
      } else {
        setSelectedImg(selectedImage - 1)
      }
    }
  }

  function moveSlidesRight() {
    track({ event: "gallery_move_right" })
    if (currentSlide <= (images.length - 1 - visibleSlides()) * slideWidth) {
      setSlide(currentSlide + slideWidth)
      setSelectedImg(selectedImage + 1)
    } else {
      if (selectedImage === images.length - 1) {
        setSlide(0)
        setSelectedImg(0)
      } else {
        setSelectedImg(selectedImage + 1)
      }
    }
  }
  const buttonStyles =
    "inline-block outline-offset-8 w-[25px] h-[14px] lg:block mx-0 pt-0 lg:absolute top-1/2 -translate-y-1/2"

  return (
    <>
      <div className="fixed z-[9999] h-[100dvh] w-[100dvw] bg-white bg-opacity-95" onClick={() => closeModal()} />
      <button
        onClick={() => closeModal()}
        aria-label="Close Modal"
        title="Close Modal"
        className="close-modal-btn fixed right-2 top-2 z-[100001] h-16 p-3"
      >
        <Close className="duration-300 hover:rotate-90" />
      </button>
      <div
        className="modal fixed z-[100000] mx-auto flex h-full w-full flex-col justify-center lg:left-[50vw] lg:top-[50vh] lg:mt-0 lg:h-auto lg:max-w-4xl lg:-translate-x-1/2 lg:-translate-y-1/2"
        ref={focusRef}
        tabIndex={0}
      >
        <Image
          src={images[selectedImage]?.filename}
          alt={images[selectedImage]?.alt}
          placeholder={images?.blurDataURL ? "blur" : "empty"}
          blurDataURL={images?.blurDataURL}
          width={878}
          height={592}
          sizes="100vw"
          className="aspect-[878/592] w-full object-cover mx-5 mb-4 lg:block rounded-10"
        />
        <div className="relative mb-4 block">
          <div className="relative flex justify-center">
            <div
              className="flex transition-all duration-500 lg:flex-row lg:gap-x-2.5"
              style={
                width >= 1024
                  ? {
                      transform: `translateX(-${currentSlide}px)`,
                      width: `${slideWidth * visibleSlides()}px`,
                    }
                  : null
              }
            >
              {images.map((image, idx) => {
                const offset = currentSlide / slideWidth
                const range = offset + visibleSlides()
                const visible = idx < range && idx >= offset

                return (
                  <div className="hidden lg:block" key={image?.filename}>
                    <button
                      className={cn(
                        "relative mb-4 h-full w-max min-w-max flex-1 transition-all duration-300 lg:mb-0 lg:h-[78px] lg:w-[118px]",
                        {
                          "lg:opacity-1": visible,
                          "lg:opacity-0": !visible,
                          "lg:border-[3px] lg:border-green": idx === selectedImage,
                        },
                      )}
                      disabled={!visible}
                      onClick={() => setSelectedImg(idx)}
                    >
                      <Image
                        src={image.filename}
                        alt={image.alt}
                        placeholder={images?.blurDataURL ? "blur" : "empty"}
                        blurDataURL={images?.blurDataURL}
                        width={118}
                        height={78}
                        sizes="(max-width: 1024px) 100vw, 10vw"
                        className="mb-4 h-full w-full object-cover lg:mb-0 lg:aspect-[118/78]"
                      />
                    </button>
                    <div className="px-6 pb-10 lg:hidden">
                      <h3 className="pb-3 text-l1 font-medium text-teal-dark lg:text-l2">{image.title}</h3>
                      <div className="">{image.name}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          {images?.length > 1 ? (
            <div className="my-4 flex w-full justify-center gap-24 text-center">
              <button className={cn(buttonStyles, "left-0 lg:left-4")} onClick={() => moveSlidesLeft()}>
                <Arrow className="rotate-90" />
              </button>
              <button className={cn(buttonStyles, "right-0 lg:right-4")} onClick={() => moveSlidesRight()}>
                <Arrow className="-rotate-90" />
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}
